import type { FC } from 'react';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import type {
  DashboardWidgetConfig,
  DashboardWidgetProps,
  UpstreamApiDashboardResult,
} from 'src/types/dashboard';
import { getAggregateStatisticValue } from 'src/services/chartDataTransformation/ChartDataTransformation';
import useGuidedTour from 'src/components/app/context/GuidedTourContext';
import { useStatistics } from 'src/components/dashboard/DashboardContext/StatisticsContext';
import ErrorOverlayWithRefetchButton from 'src/components/dashboard/DashboardPage/components/ErrorOverlayWithRefetchButton';
import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import {
  getAggregateByQuestion,
  getComparisonData,
} from 'src/components/dashboard/helper';
import useWidgetChartConfig from 'src/components/dashboard/hooks/useWidgetChartConfig';

import { styles } from './index.styles';

export const BAR_TABLE_WIDGET_TABLE_TEST_ID = 'bar-table-widget-table';
export const BAR_TABLE_WIDGET_BAR_TEST_ID = 'bar-table-widget-bar';

/**
 * Statistics shown when the widget carries no `dataseries` configuration:
 * top 2 box and its change to last year.
 */
export const DEFAULT_TABLE_ATTRIBUTES = ['ptop2', 'ptop2_yoyDiff'] as const;

/**
 * Value and year-over-year statistic configured via `dataseries.content.table`
 * (either ptop2/ptop2_yoyDiff or mean/mean_yoyDiff, see `DATASERIES_SCHEMA`).
 *
 * Returns `undefined` for an unconfigured widget, so callers can tell an explicitly
 * configured `ptop2` from the default.
 */
const getConfiguredTableAttributes = (
  config: DashboardWidgetConfig,
): readonly [string, string] | undefined => {
  const [value, difference] = config.dataseries?.content?.table ?? [];

  return value?.attribute && difference?.attribute
    ? [value.attribute, difference.attribute]
    : undefined;
};

/**
 * Function for transforming the reporting_engine data to a table with labels for columns and rows.
 */
export const getTableData = (
  config: DashboardWidgetConfig,
  dashboardResults: UpstreamApiDashboardResult[],
) => {
  const dashboardResultsWithHeadcount = dashboardResults.filter((value) =>
    value.org_units.every((value1) => value1.headcount),
  );

  if (!dashboardResultsWithHeadcount.length)
    return { aggregateLabels: [], orgUnitNames: [], comparisons: [] };

  const orgUnitNames: string[] = new Array(
    dashboardResultsWithHeadcount.length,
  );
  const aggregateLabels: string[] = new Array(config.questions.length);
  const comparisons: [
    number | null | undefined,
    number | null | undefined,
  ][][] = new Array(config.questions.length);
  const [valueAttribute, differenceAttribute] =
    getConfiguredTableAttributes(config) ?? DEFAULT_TABLE_ATTRIBUTES;

  config.questions.forEach((question, questionIndex) => {
    comparisons[questionIndex] = new Array(
      dashboardResultsWithHeadcount.length,
    ); // Initialize the row, empty values are allowed.

    const firstDashboardResultWithAggregates =
      dashboardResultsWithHeadcount.find(
        (dashboardResultsWithHeadcount: UpstreamApiDashboardResult) =>
          dashboardResultsWithHeadcount.aggregates.length,
      );

    if (firstDashboardResultWithAggregates) {
      const focalQuestion = getAggregateByQuestion(
        question,
        firstDashboardResultWithAggregates,
      );

      dashboardResultsWithHeadcount.forEach(
        (dashboardResult, dashboardResultIndex: number) => {
          const aggregate = getAggregateByQuestion(
            focalQuestion?.question,
            dashboardResult,
          );

          // Fill labels in first iteration.
          if (questionIndex === 0)
            orgUnitNames[dashboardResultIndex] =
              dashboardResult.org_units[0].name;
          if (aggregate && dashboardResultIndex === 0)
            aggregateLabels[questionIndex] = aggregate.label;

          comparisons[questionIndex][dashboardResultIndex] = aggregate
            ? [
                getAggregateStatisticValue(aggregate, valueAttribute),
                getAggregateStatisticValue(aggregate, differenceAttribute),
              ]
            : [undefined, undefined];
        },
      );
    }
  });

  return { aggregateLabels, orgUnitNames, comparisons };
};

const BarTableWidget: FC<DashboardWidgetProps> = ({ config }) => {
  const { t } = useTranslation('dashboard');
  const { data, loading, errorMsg, refetch } = useStatistics();
  const { createStep } = useGuidedTour();
  const guidedTour = useMemo(
    () => createStep(config.id, config.guided_tour_step),
    [config.guided_tour_step],
  );
  const { xAxisMax } = useWidgetChartConfig(config);
  // Percentages have no configured scale and span 0–100.
  const scaleMax = xAxisMax && xAxisMax > 0 ? xAxisMax : 100;
  // Widgets without a dataseries keep the legacy label key, so existing environment
  // overrides survive.
  const tableLabel = useMemo(() => {
    if (config.widget_text) return config.widget_text;

    const configuredAttributes = getConfiguredTableAttributes(config);

    return configuredAttributes
      ? t('Labels.StatisticAndDiffLastYear', {
          statistic: t(`Statistics.${configuredAttributes[0]}`),
        })
      : t('Labels.FavAndDiffLastYear');
  }, [config, t]);
  const { aggregateLabels, orgUnitNames, comparisons } = useMemo(() => {
    // Special case in this widget. If multiple OrgUnits are selected, use them as comparison datalines.
    if (data?.selected_results.length) {
      const comparisonData = [...data.selected_results];

      return getTableData(config, comparisonData);
    }

    return getTableData(config, data ? getComparisonData(config, data) : []);
  }, [config, data]);

  if (loading || errorMsg) {
    return (
      <Box sx={{ position: 'relative' }}>
        <Stack
          direction="column"
          spacing={2}
          data-test-id="loading-bar-table"
          sx={{ p: 2, position: 'relative' }}
        >
          <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
            {config.name}
          </Typography>
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
          <Skeleton variant="rectangular" height={108} />
        </Stack>
        {errorMsg && (
          <ErrorOverlayWithRefetchButton
            errorMsg={errorMsg}
            refetch={refetch}
            name={config.name}
          />
        )}
      </Box>
    );
  }
  return (
    <Box className="BarTableWidget-root" sx={styles.barTableWidget}>
      <Stack direction="row" spacing={1.25}>
        {guidedTour}
        <Typography gutterBottom variant="h3" sx={{ fontWeight: 'bold' }}>
          {config.name}
        </Typography>
      </Stack>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell
                className="BarTableWidget-firstColumn"
                sx={styles.firstColumn}
              >
                <Typography
                  sx={{
                    fontWeight: 'bold',
                    overflowWrap: 'anywhere',
                  }}
                >
                  {tableLabel}
                </Typography>
              </TableCell>

              {aggregateLabels.map((aggregateLabel) => (
                <TableCell key={aggregateLabel} align="left">
                  <Tooltip
                    arrow
                    enterTouchDelay={1}
                    leaveTouchDelay={3000}
                    placement="bottom"
                    title={aggregateLabel}
                  >
                    <Typography sx={styles.rotateText}>
                      {aggregateLabel}
                    </Typography>
                  </Tooltip>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {orgUnitNames.map((orgUnitName, orgUnitNameIndex) => (
              <TableRow
                key={orgUnitName}
                data-test-id={BAR_TABLE_WIDGET_TABLE_TEST_ID}
              >
                <TableCell
                  align="left"
                  className="BarTableWidget-firstColumn"
                  sx={styles.firstColumn}
                >
                  <Tooltip
                    arrow
                    enterTouchDelay={1}
                    leaveTouchDelay={3000}
                    placement="top"
                    title={orgUnitName}
                  >
                    <Typography sx={styles.firstColumnText}>
                      {orgUnitName}
                    </Typography>
                  </Tooltip>
                </TableCell>

                {comparisons.map((comparison, comparisonIndex) => {
                  const comparisonCount = comparison[orgUnitNameIndex][0];
                  const comparisonDiff = comparison[orgUnitNameIndex][1];

                  return (
                    <TableCell key={comparisonIndex} align="left">
                      <Box
                        className="BarTableWidget-bar"
                        data-test-id={BAR_TABLE_WIDGET_BAR_TEST_ID}
                        sx={styles.bar({
                          comparisonCount,
                          comparisonDiff,
                          scaleMax,
                        })}
                      />

                      <Typography
                        sx={{ display: 'flex', alignItems: 'center' }}
                      >
                        {comparisonCount}

                        <NumberDifference
                          comparisonDiff={comparisonDiff}
                          showArrow
                        />
                      </Typography>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default BarTableWidget;
